// Free, keyless sources: OpenFreeMap's vector plans (with their buildings) and the open terrain tiles on AWS.
const STYLE_URL   = 'https://tiles.openfreemap.org/styles/liberty'
const TERRAIN_URL = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'
const LINE_COLOR  = '#1e3a8a'
const GPS_COLOR   = '#fc4c02'
const STOP_PAUSE  = 2400
const STATUS_COLORS = { open: '#ef5350', scheduled: '#f4c20d', in_progress: '#3b8de0', closed: '#4cc35e' }

const haversine = (a, b) => {
  const toRad = degrees => degrees * Math.PI / 180
  const dLat  = toRad(b[0] - a[0])
  const dLng  = toRad(b[1] - a[1])
  const h     = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.sqrt(h))
}

const bearingBetween = (a, b) => {
  const toRad = degrees => degrees * Math.PI / 180
  const y = Math.sin(toRad(b[1] - a[1])) * Math.cos(toRad(b[0]))
  const x = Math.cos(toRad(a[0])) * Math.sin(toRad(b[0])) - Math.sin(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.cos(toRad(b[1] - a[1]))
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360
}

const lerpAngle = (from, to, t) => {
  const delta = ((to - from + 540) % 360) - 180
  return (from + delta * t + 360) % 360
}

const zoomFor = metres => {
  if (metres < 3000)   return 16
  if (metres < 10000)  return 15
  if (metres < 40000)  return 13
  if (metres < 150000) return 11
  return 9
}

const lngLat = point => [point[1], point[0]]
const parse  = iso => (iso ? Date.parse(iso) : NaN)

// Each leg and stop carries the clock span the plan gives it, so a real GPS track replays at the same pace.
const buildTimeline = tour => {
  const legs = tour.legs.map(leg => {
    const cums = []
    let total = 0
    for (let i = 1; i < leg.coordinates.length; i++) {
      total += haversine(leg.coordinates[i - 1], leg.coordinates[i])
      cums.push(total)
    }
    return { leg, cums, total }
  })
  const longest  = Math.max(...legs.map(item => item.total), 1)
  const timeline = []
  let at = 0
  legs.forEach((item, i) => {
    const leg      = item.leg
    const fromStop = tour.stops.find(stop => stop.index === leg.from)
    const toStop   = tour.stops.find(stop => stop.index === leg.to)
    const road     = ((toStop ? toStop.travel : tour.stops[tour.stops.length - 1].back) || leg.seconds || 1800) * 1000
    const arrival  = toStop ? parse(toStop.at) : parse(tour.stops[tour.stops.length - 1].until || tour.stops[tour.stops.length - 1].at) + road
    const leave    = fromStop ? Math.min(parse(fromStop.until || fromStop.at), arrival - road) : arrival - road
    const duration = 3500 + 10000 * (item.total / longest)
    timeline.push({ kind: 'leg', i, start: at, end: at + duration, zoom: zoomFor(item.total), clockStart: leave, clockEnd: arrival })
    at += duration
    if (toStop) {
      timeline.push({ kind: 'stop', i, stop: toStop, start: at, end: at + STOP_PAUSE, clockStart: parse(toStop.at), clockEnd: parse(toStop.until || toStop.at) })
      at += STOP_PAUSE
    }
  })
  return { legs, timeline, total: at }
}

const along = (item, fraction) => {
  const coordinates = item.leg.coordinates
  if (coordinates.length < 2 || fraction <= 0) return { point: coordinates[0], index: 0 }
  if (fraction >= 1) return { point: coordinates[coordinates.length - 1], index: coordinates.length - 1 }
  const target = item.total * fraction
  let i = item.cums.findIndex(cum => cum >= target)
  if (i < 0) i = item.cums.length - 1
  const before = i === 0 ? 0 : item.cums[i - 1]
  const ratio  = (target - before) / Math.max(item.cums[i] - before, 1e-9)
  const [a, b] = [coordinates[i], coordinates[i + 1]]
  return { point: [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio], index: i }
}

// Pins and the van are images of the map itself, not DOM markers.
const pinImage = (color, number) => {
  const scale  = 2
  const canvas = document.createElement('canvas')
  canvas.width  = 30 * scale
  canvas.height = 38 * scale
  const ctx = canvas.getContext('2d')
  ctx.scale(scale, scale)
  const path = new Path2D('M15 0C6.7 0 0 6.7 0 15c0 10.5 15 23 15 23s15-12.5 15-23C30 6.7 23.3 0 15 0z')
  ctx.fillStyle = color
  ctx.fill(path)
  ctx.lineWidth = 2.5
  ctx.strokeStyle = '#fff'
  ctx.stroke(path)
  ctx.fillStyle = '#fff'
  ctx.font = '700 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(String(number), 15, 15)
  return ctx.getImageData(0, 0, canvas.width, canvas.height)
}

const dotImage = (fill, stroke, glyph) => {
  const scale  = 2
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 34 * scale
  const ctx = canvas.getContext('2d')
  ctx.scale(scale, scale)
  ctx.beginPath()
  ctx.arc(17, 17, 14, 0, Math.PI * 2)
  ctx.fillStyle = fill
  ctx.fill()
  ctx.lineWidth = 3
  ctx.strokeStyle = stroke
  ctx.stroke()
  if (glyph === 'van') {
    ctx.fillStyle = stroke
    ctx.fillRect(9, 12, 12, 9)
    ctx.fillRect(21, 15, 5, 6)
    ctx.beginPath(); ctx.arc(12, 22, 2.2, 0, Math.PI * 2); ctx.arc(22, 22, 2.2, 0, Math.PI * 2); ctx.fill()
  }
  return ctx.getImageData(0, 0, canvas.width, canvas.height)
}

// MapLibre runs in a worker it loads by URL; webpack emits the worker and the shared chunk it imports side by side.
const WORKER_URL = new URL('maplibre-gl/dist/maplibre-gl-worker.mjs', import.meta.url)
new URL('maplibre-gl/dist/maplibre-gl-shared.mjs', import.meta.url)

export const openFlyover3d = async ({ tour, labels, speed = 1 }) => {
  const [maplibregl] = await Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl.css')])
  maplibregl.setWorkerUrl(WORKER_URL.href)

  const hasTrack = tour.track && tour.track.length > 1
  const overlay  = document.createElement('div')
  overlay.className = 'fly3d'
  overlay.innerHTML = `
    <div class="fly3d__map"></div>
    <div class="fly3d__top">
      <div class="fly3d__title"><span class="fly3d__dot" style="background:${tour.color}"></span>${tour.name}${hasTrack ? `<span class="fly3d__legend"><i style="background:${GPS_COLOR}"></i>${labels.track}</span>` : ''}</div>
      <button type="button" class="fly3d__close" aria-label="${labels.close}">&times;</button>
    </div>
    <div class="fly3d__player">
      <button type="button" class="fly3d__play" title="${labels.pause}"><i class="fas fa-pause"></i></button>
      <button type="button" class="fly3d__speed" title="${labels.speed}">${speed}×</button>
      <input type="range" class="fly3d__bar" min="0" max="1000" value="0" aria-label="${labels.play}">
      <span class="fly3d__stop"></span>
    </div>`
  document.body.appendChild(overlay)
  document.body.classList.add('fly3d-open')

  const path = tour.legs.flatMap((leg, i) => (i ? leg.coordinates.slice(1) : leg.coordinates))
  const { legs, timeline, total } = buildTimeline(tour)
  const legStart = []
  let index = 0
  tour.legs.forEach((leg, i) => { legStart.push(i ? index - 1 : 0); index += i ? leg.coordinates.length - 1 : leg.coordinates.length })
  const reachAt = new Map()
  tour.legs.forEach((leg, i) => {
    if (i === 0) { const first = tour.stops.find(stop => stop.index === leg.from); if (first) reachAt.set(String(first.id), 0) }
    const stop = tour.stops.find(stop => stop.index === leg.to)
    if (stop) reachAt.set(String(stop.id), legStart[i] + leg.coordinates.length - 1)
  })
  const track = hasTrack ? tour.track.map(point => ({ at: parse(point.at), lngLat: [point.lng, point.lat] })).filter(point => !isNaN(point.at)) : []

  const map = new maplibregl.Map({
    container:   overlay.querySelector('.fly3d__map'),
    style:       STYLE_URL,
    center:      lngLat(path[0]),
    zoom:        timeline[0].zoom,
    pitch:       60,
    bearing:     path.length > 1 ? bearingBetween(path[0], path[Math.min(10, path.length - 1)]) : 0,
    interactive: false,
    attributionControl: { compact: true }
  })

  const state = { t: 0, speed, playing: false, last: null, bearing: map.getBearing(), frame: null, done: false, drawn: [], shown: new Set(), van: lngLat(path[0]), gps: null, gpsLine: [] }
  const play   = overlay.querySelector('.fly3d__play')
  const speedB = overlay.querySelector('.fly3d__speed')
  const bar    = overlay.querySelector('.fly3d__bar')
  const stopEl = overlay.querySelector('.fly3d__stop')

  const feature = (type, coordinates) => ({ type: 'Feature', properties: {}, geometry: { type, coordinates } })
  const stopFeatures = () => ({
    type: 'FeatureCollection',
    features: tour.stops.filter(stop => state.shown.has(String(stop.id))).map(stop => ({ type: 'Feature', properties: { icon: `pin-${stop.id}` }, geometry: { type: 'Point', coordinates: [stop.lng, stop.lat] } }))
  })

  const install = () => {
    tour.stops.forEach(stop => {
      const color = STATUS_COLORS[stop.state] || STATUS_COLORS.scheduled
      if (!map.hasImage(`pin-${stop.id}`)) map.addImage(`pin-${stop.id}`, pinImage(color, stop.index), { pixelRatio: 2 })
    })
    if (!map.hasImage('van')) map.addImage('van', dotImage('#fff', LINE_COLOR, 'van'), { pixelRatio: 2 })
    if (!map.hasImage('gps')) map.addImage('gps', dotImage(GPS_COLOR, '#fff'), { pixelRatio: 2 })

    map.addSource('terrain', { type: 'raster-dem', tiles: [TERRAIN_URL], encoding: 'terrarium', tileSize: 256, maxzoom: 15 })
    map.setTerrain({ source: 'terrain', exaggeration: 1.2 })
    map.addSource('tour-full',  { type: 'geojson', data: feature('LineString', path.map(lngLat)) })
    map.addSource('tour-drawn', { type: 'geojson', data: feature('LineString', state.drawn) })
    map.addSource('gps-line',   { type: 'geojson', data: feature('LineString', state.gpsLine) })
    map.addSource('stops',      { type: 'geojson', data: stopFeatures() })
    map.addSource('van',        { type: 'geojson', data: feature('Point', state.van) })
    map.addSource('gps-now',    { type: 'geojson', data: { type: 'FeatureCollection', features: state.gps ? [feature('Point', state.gps)] : [] } })
    map.addLayer({ id: 'tour-full',  type: 'line', source: 'tour-full',  paint: { 'line-color': LINE_COLOR, 'line-width': 3, 'line-opacity': .25 }, layout: { 'line-cap': 'round', 'line-join': 'round' } })
    map.addLayer({ id: 'gps-line',   type: 'line', source: 'gps-line',   paint: { 'line-color': GPS_COLOR, 'line-width': 4, 'line-opacity': .9, 'line-dasharray': [1, 2] }, layout: { 'line-cap': 'round', 'line-join': 'round' } })
    map.addLayer({ id: 'tour-drawn', type: 'line', source: 'tour-drawn', paint: { 'line-color': LINE_COLOR, 'line-width': 6, 'line-opacity': .95 }, layout: { 'line-cap': 'round', 'line-join': 'round' } })
    map.addLayer({ id: 'stops',   type: 'symbol', source: 'stops',   layout: { 'icon-image': ['get', 'icon'], 'icon-anchor': 'bottom', 'icon-allow-overlap': true, 'icon-ignore-placement': true } })
    map.addLayer({ id: 'gps-now', type: 'symbol', source: 'gps-now', layout: { 'icon-image': 'gps', 'icon-allow-overlap': true, 'icon-ignore-placement': true } })
    map.addLayer({ id: 'van',     type: 'symbol', source: 'van',     layout: { 'icon-image': 'van', 'icon-allow-overlap': true, 'icon-ignore-placement': true } })
  }

  const setData = (id, data) => { const source = map.getSource(id); if (source) source.setData(data) }

  const onKey = e => { if (e.key === 'Escape') close() }
  const close = () => {
    cancelAnimationFrame(state.frame)
    map.remove()
    overlay.remove()
    document.body.classList.remove('fly3d-open')
    document.removeEventListener('keydown', onKey)
  }
  document.addEventListener('keydown', onKey)
  overlay.querySelector('.fly3d__close').addEventListener('click', close)

  const setPlaying = playing => {
    state.playing = playing
    state.last    = null
    play.innerHTML = playing ? '<i class="fas fa-pause"></i>' : state.done ? '<i class="fas fa-redo"></i>' : '<i class="fas fa-play"></i>'
    play.title     = playing ? labels.pause : labels.play
    if (playing) state.frame = requestAnimationFrame(frame)
    else cancelAnimationFrame(state.frame)
  }
  play.addEventListener('click', () => {
    if (state.done) { restart(); return }
    setPlaying(!state.playing)
  })
  speedB.addEventListener('click', () => {
    state.speed = { 1: 2, 2: 4, 4: 1 }[state.speed] || 1
    speedB.textContent = `${state.speed}×`
  })
  bar.addEventListener('input', () => {
    state.t = bar.value / 1000 * total
    state.last = null
    state.done = false
    state.shown.clear()
    apply()
  })

  const restart = () => {
    state.t = 0
    state.done = false
    state.shown.clear()
    setData('stops', stopFeatures())
    apply()
    setPlaying(true)
  }

  const apply = () => {
    const segment = timeline.find(item => state.t < item.end) || timeline[timeline.length - 1]
    const item    = legs[segment.i]
    const share   = Math.min(Math.max((state.t - segment.start) / (segment.end - segment.start), 0), 1)
    let point, idx
    if (segment.kind === 'leg') {
      ({ point, index: idx } = along(item, share))
      const ahead = item.leg.coordinates[Math.min(idx + 2, item.leg.coordinates.length - 1)]
      if (ahead && (ahead[0] !== point[0] || ahead[1] !== point[1])) state.bearing = lerpAngle(state.bearing, bearingBetween(point, ahead), .05)
      stopEl.textContent = ''
    } else {
      idx   = item.leg.coordinates.length - 1
      point = item.leg.coordinates[idx]
      const time = segment.stop.at ? new Date(segment.stop.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
      stopEl.textContent = `${segment.stop.index} · ${time}`.trim()
    }
    const pathIndex = legStart[segment.i] + idx
    state.drawn = path.slice(0, pathIndex + 1).concat([point]).map(lngLat)
    setData('tour-drawn', feature('LineString', state.drawn))

    let revealed = false
    reachAt.forEach((at, key) => { if (!state.shown.has(key) && pathIndex >= at) { state.shown.add(key); revealed = true } })
    if (revealed) setData('stops', stopFeatures())

    state.van = lngLat(point)
    setData('van', feature('Point', state.van))

    if (track.length && !isNaN(segment.clockStart) && !isNaN(segment.clockEnd)) {
      const clock = segment.clockStart + (segment.clockEnd - segment.clockStart) * share
      const seen  = track.filter(sample => sample.at <= clock)
      state.gpsLine = seen.map(sample => sample.lngLat)
      state.gps     = seen.length ? seen[seen.length - 1].lngLat : null
      setData('gps-line', feature('LineString', state.gpsLine.length > 1 ? state.gpsLine : []))
      setData('gps-now', { type: 'FeatureCollection', features: state.gps ? [feature('Point', state.gps)] : [] })
    }

    const zoom = segment.kind === 'leg' ? segment.zoom : map.getZoom()
    map.jumpTo({ center: lngLat(point), zoom: map.getZoom() + (zoom - map.getZoom()) * .05, bearing: state.bearing, pitch: segment.kind === 'stop' ? 65 : 60 })
    bar.value = Math.round(state.t / total * 1000)
  }

  const frame = now => {
    if (!state.playing) return
    if (state.last != null) state.t += (now - state.last) * state.speed
    state.last = now
    if (state.t >= total) {
      state.t = total
      apply()
      state.done = true
      setPlaying(false)
      const bounds = path.reduce((box, p) => box.extend(lngLat(p)), new maplibregl.LngLatBounds(lngLat(path[0]), lngLat(path[0])))
      map.fitBounds(bounds, { padding: 80, pitch: 45, bearing: 0, duration: 2500, maxZoom: 15 })
      return
    }
    apply()
    state.frame = requestAnimationFrame(frame)
  }

  map.on('load', () => {
    install()
    apply()
    setTimeout(() => setPlaying(true), 600)
  })

  return { close }
}
